import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import { Context } from 'components/Store';
import PlusIcon from 'assets/plus.svg?react';
import CwFPagination from 'components/CwFPagination';
import LoadingSpinner from 'components/LoadingSpinner';
import SortableTable from 'components/UiKit/SortableTable';
import useInventoryProduct from 'screens/ProductList/hooks/useInventoryProduct';
import ProductListTableCell from '../ProductListTableCell';
import RemoveProductModal from '../RemoveProductModal';
import './index.css';

export const AddProductButton = ({ onClick }) => (
  <Button
    className="mt-1 px-3"
    id="add-product-button"
    type={ButtonType.primary}
    onClick={onClick}
    icon={<PlusIcon />}
  >
    <span>Add Product</span>
  </Button>
);

const productListTableDefaultPropOnSort = () => false;
const ProductListTable = ({
  products,
  productsCount,
  isLoading,
  sortBy = '',
  sortDir = '',
  loadingMessage = '',
  pageNo,
  setPageNo,
  onSort = productListTableDefaultPropOnSort,
  emptyState,
  handleOnEdit,
  setToastType,
  setToastMessage,
  setShowToast,
  refreshProducts
}) => {
  const [
    { toastType, toastMessage, showToastRemoveProduct, removeProductFailure }
  ] = useContext(Context);

  const { isRemoveLoading, removeProduct, removeError } = useInventoryProduct();

  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [productToRemove, setProductToRemove] = useState(null);
  const [toastHasNotBeenClosed, setToastHasNotBeenClosed] = useState(true);

  const TABLE_COLUMNS = [
    {
      field: 'productName',
      title: `Products (${productsCount})`,
      sortable: true
    },
    {
      field: 'productType',
      title: 'Type',
      sortable: true
    },
    {
      field: 'available',
      title: 'Available Quantity',
      sortable: true
    },
    {
      field: 'averagePricePerUnit',
      title: 'List Price',
      sortable: true
    },
    {
      field: 'productId',
      title: '',
      sortable: false
    }
  ];

  const handlePageChange = page => {
    setPageNo(page);
  };

  const openRemoveModal = () => {
    setShowRemoveModal(true);
  };

  const handleOnRemove = product => {
    setProductToRemove(product);
    openRemoveModal();
  };

  const closeRemoveModal = () => {
    setShowRemoveModal(false);
  };

  // LandingTable's simple sort mode: a newly clicked column starts descending
  // and a repeated click toggles, unlike the wrapper's ascending-first default.
  const handleSort = field => {
    if (!isLoading) {
      onSort(field, sortBy !== field || sortDir === 'asc' ? 'desc' : 'asc');
    }
  };

  useEffect(() => {
    if (showToastRemoveProduct && !removeProductFailure) {
      setToastType(toastType);
      setToastMessage(toastMessage);
      setShowToast(true);
    } else if (removeProductFailure) {
      setToastType('error');
      setToastMessage('Unable to remove product.');
      setShowToast(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToastRemoveProduct, toastMessage, toastType]);

  const handleRemove = async () => {
    try {
      await removeProduct(productToRemove?.productId);
    } finally {
      if (!removeError) {
        setShowRemoveModal(false);
        setToastType('success');
        setToastMessage('Product removed.');
      } else {
        setShowRemoveModal(false);
        setToastType('error');
        setToastMessage('Unable to remove product.');
      }
    }
    setShowToast(true);
    refreshProducts();
  };

  const isTableEmpty = !isLoading && products?.length === 0;

  const renderCell = (field, product) => (
    <ProductListTableCell
      field={field}
      product={product}
      onEdit={handleOnEdit}
      onRemove={handleOnRemove}
    />
  );

  const renderTable = () => {
    if (isTableEmpty) {
      return emptyState;
    }
    return (
      <>
        <SortableTable
          columns={TABLE_COLUMNS}
          rows={isLoading ? [] : products}
          renderCell={renderCell}
          sortBy={sortBy}
          sortDir={sortDir || null}
          onSort={handleSort}
        />
        {isLoading && (
          <div
            className="product-list-table__loading flex flex-col items-center justify-center"
            data-testid="product-list-loading"
          >
            <LoadingSpinner
              message={loadingMessage ?? ''}
              size={75}
              label="Loading products"
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div
      data-testid="product-list-table"
      className={`product-list-table h-full min-h-0 w-full flex flex-col ${
        isLoading ? 'product-list-table--loading' : ''
      }`}
    >
      <div
        className="product-list-table__viewport relative flex-1 min-h-0 overflow-auto"
        aria-busy={isLoading}
      >
        {renderTable()}
      </div>
      {!isTableEmpty && (
        <div
          className={`product-list-table__pagination shrink-0 pr-6 border-t border-neutral-20 ${
            isLoading ? 'pointer-events-none cursor-not-allowed' : ''
          }`}
        >
          {CwFPagination(productsCount, pageNo, handlePageChange)}
        </div>
      )}
      <RemoveProductModal
        open={showRemoveModal}
        itemType="Product"
        close={closeRemoveModal}
        handleRemove={handleRemove}
        isRemoveLoading={isRemoveLoading}
        setToastHasNotBeenClosed={setToastHasNotBeenClosed}
        toastHasNotBeenClosed={toastHasNotBeenClosed}
        toastRenderContents={null}
      />
    </div>
  );
};

AddProductButton.propTypes = {
  onClick: PropTypes.func.isRequired
};

ProductListTable.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  productsCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadingMessage: PropTypes.string,
  pageNo: PropTypes.number.isRequired,
  setPageNo: PropTypes.func.isRequired,
  emptyState: PropTypes.element.isRequired,
  handleOnEdit: PropTypes.func.isRequired,
  setToastType: PropTypes.func.isRequired,
  setToastMessage: PropTypes.func.isRequired,
  setShowToast: PropTypes.func.isRequired,
  refreshProducts: PropTypes.func.isRequired,
  sortBy: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func
};

export default ProductListTable;
